import { useEffect, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { coversApi } from '@/api/covers';
import { reportsApi } from '@/api/reports';
import { TemplateSelectionModal } from '@/components/modals/TemplateSelectionModal';
import { TemplateUpsellModal } from '@/components/modals/TemplateUpsellModal';
import { Patient, ReportData, ReportTemplate } from '@/components/preview/types';
import { useModalContext } from '@/components/ui/modal-provider';
import { SECTIONS } from '@/components/wizard/constants';
import { useAuth } from '@/context/AuthContext';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture } from '@/lib/posthog';
import { ReportTemplatePreviewPage } from '@/pages/constants';
import { type User } from '@/types/user';
import { INITIAL_FORM_DATA } from '../constants';
import { loadReport } from '../utils/loadReport';
import {
    CreateReportLocationState,
    TemplatePreviewLocationState,
    buildReportDataFromTemplate,
    clearPendingTemplateState,
} from '../utils/template';
import type { Cover, ReportCover } from '@/types/cover';

interface UseReportInitializationProps {
    reportId: string | null;
    preSelectedPatient?: Patient;
    setValues: (values: ReportData, shouldValidate?: boolean) => void;
}

type PendingTemplatePrompt = {
    templates: ReportTemplate[];
    patientData: Patient | undefined;
};

export const useReportInitialization = ({
    reportId,
    preSelectedPatient,
    setValues,
}: UseReportInitializationProps) => {
    const { openModal, openWizardModal, closeModal } = useModalContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isLoading: isAuthLoading } = useAuth();
    const platformTemplateAccess = useFeatureEntitlement('platformTemplates.use');
    const coverAccess = useFeatureEntitlement('reports.cover');

    const [isLoading, setIsLoading] = useState(true);
    const hasInitializedDefaultsRef = useRef(false);
    const autoAppliedCoverRef = useRef<ReportCover | null>(null);
    const [pendingTemplatePrompt, setPendingTemplatePrompt] =
        useState<PendingTemplatePrompt | null>(null);

    useEffect(() => {
        const init = async () => {
            // BRANCH 1: EXISTING REPORT (HAS ID)
            if (reportId) {
                await handleExistingReport();
                return;
            }

            // BRANCH 2: NEW REPORT (NO ID)
            await handleNewReport();
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        reportId,
        preSelectedPatient,
        setValues,
        user,
        isAuthLoading,
        openModal,
        closeModal,
        navigate,
    ]);

    const handleExistingReport = async () => {
        if (!reportId) return;

        const loadedReport = await loadReport(reportId);
        setValues(loadedReport);
        setIsLoading(false);
    };

    const handleNewReport = async () => {
        // Prevent re-initialization if already done
        if (hasInitializedDefaultsRef.current) {
            setIsLoading(false);
            return;
        }
        if (isAuthLoading) return;

        hasInitializedDefaultsRef.current = true;

        const patientData = preparePatientData(preSelectedPatient);

        // Fetch Defaults, Templates & the default cover in parallel. Fora do plano
        // a capa nunca é aplicada ao laudo (a API 403 o save) — só previsualizada
        // na aba Capa, então nem buscamos a capa padrão aqui.
        const [defaults, templates, defaultCover] = await Promise.all([
            reportsApi.getDefaults().catch(() => []),
            reportsApi.getTemplates().catch(() => []),
            coverAccess.shouldBlock
                ? Promise.resolve(null)
                : coversApi
                      .list()
                      .then((covers) => covers.find((cover) => cover.is_default) ?? null)
                      .catch(() => null),
        ]);

        autoAppliedCoverRef.current = defaultCover
            ? {
                  ...defaultCover.config,
                  cover_id: defaultCover.id,
                  applied_at: new Date().toISOString(),
              }
            : null;

        // Construct Data with Defaults & Customizations
        const defaultReportData = prepareDefaultReportData(defaults, patientData, user);

        // Set Initial Values (Defaults + Customizations)
        setValues(defaultReportData as ReportData);
        setIsLoading(false);

        notifyCoverAutoApply(defaultCover);

        const pendingTemplateId = (location.state as CreateReportLocationState | undefined)
            ?.pendingTemplateId;

        if (pendingTemplateId) {
            const pendingTemplate = templates.find((template) => template.id === pendingTemplateId);

            navigate(
                {
                    pathname: location.pathname,
                    search: location.search,
                },
                {
                    replace: true,
                    state: clearPendingTemplateState(
                        location.state as CreateReportLocationState | undefined,
                    ),
                },
            );

            if (pendingTemplate) {
                await applyTemplate(pendingTemplate, patientData);
                return;
            }
        }

        setPendingTemplatePrompt({ templates, patientData });
    };

    const preparePatientData = (patient?: Patient): Patient | undefined => {
        return patient
            ? ({
                  id: patient.id,
                  full_name: patient.full_name,
                  date_of_birth: patient.date_of_birth,
                  gender: patient.gender,
                  city: patient.city,
                  education_level: patient.education_level,
                  referring_professional: patient.referring_professional,
                  email: patient.email || '',
                  phone: patient.phone || '',
                  emergency_contact_name: '',
                  emergency_contact_phone: '',
                  address: '',
                  avatar_url: patient.avatar_url,
              } as Patient)
            : undefined;
    };

    const prepareDefaultReportData = (
        defaults: Array<{ section_type: string; content?: string }>,
        patientData: Patient | undefined,
        user: User | null,
    ): Partial<ReportData> => {
        const defaultReportData: Partial<ReportData> = {
            ...INITIAL_FORM_DATA,
            patient: patientData,
        };

        const TEXT_SECTIONS: string[] = [
            SECTIONS.DEMAND_DESCRIPTION,
            SECTIONS.PROCEDURES,
            SECTIONS.BEHAVIOR_OBSERVATIONS,
            SECTIONS.CLINICAL_HISTORY,
            SECTIONS.DIAGNOSIS,
            SECTIONS.RECOMMENDATIONS,
        ];

        // Apply Defaults
        defaults.forEach((section) => {
            if (TEXT_SECTIONS.includes(section.section_type)) {
                const key = section.section_type as keyof ReportData;
                const textSections = defaultReportData as Record<
                    string,
                    { content?: string } | undefined
                >;
                textSections[key] = {
                    ...(textSections[key] || {}),
                    content: section.content,
                };
            }
        });

        // Apply User Customizations (Theme Override)
        if (user?.report_customizations) {
            const customs = user.report_customizations;
            // User preferences (ReportCustomizationPreferences) carry optional
            // fields; the report's customizations require them, so merge then
            // cast back to the report's customization shape.
            defaultReportData.customizations = {
                ...defaultReportData.customizations,
                colors: customs.colors || defaultReportData.customizations?.colors,
                font: customs.font || defaultReportData.customizations?.font,
                footer: {
                    ...defaultReportData.customizations?.footer,
                    ...customs.footer,
                },
                header: {
                    ...defaultReportData.customizations?.header,
                    ...customs.header,
                    logo_url:
                        customs.header?.logo_url ??
                        defaultReportData.customizations?.header?.logo_url,
                },
            } as ReportData['customizations'];
        }

        // Auto-apply the default cover: new laudos open with the cover
        // present and the patient/date already resolved.
        if (autoAppliedCoverRef.current) {
            defaultReportData.customizations = {
                ...defaultReportData.customizations,
                cover: autoAppliedCoverRef.current,
            } as ReportData['customizations'];
        }

        return defaultReportData;
    };

    const notifyCoverAutoApply = (defaultCover: Cover | null) => {
        if (!defaultCover) return;

        const toastKey = 'cover_autoapply_toast_shown';
        if (window.localStorage.getItem(toastKey) === 'true') return;

        window.localStorage.setItem(toastKey, 'true');
        toast.info('Capa aplicada automaticamente — você pode ajustar na aba Capa.');
    };

    const openTemplatePrompt = (templates: ReportTemplate[], patientData: Patient | undefined) => {
        if (templates.length > 0) {
            // This modal is the first thing a new report shows. Whether people
            // pick a template or dismiss it and then stall is one of the more
            // likely explanations for reports that never get written.
            capture('template_prompt_shown', { template_count: templates.length });

            openWizardModal({
                content: (
                    <TemplateSelectionModal
                        title="Escolha um ponto de partida"
                        onSkip={() => {
                            capture('template_prompt_dismissed', {});
                            closeModal();
                        }}
                        templates={templates}
                        onPreview={(template: ReportTemplate) => {
                            closeModal();
                            navigate(
                                `/${generatePath(ReportTemplatePreviewPage, {
                                    templateId: template.id,
                                })}`,
                                {
                                    state: {
                                        returnTo: {
                                            pathname: location.pathname,
                                            search: location.search,
                                            state: (location.state || undefined) as
                                                | CreateReportLocationState
                                                | undefined,
                                        },
                                    } as TemplatePreviewLocationState,
                                },
                            );
                        }}
                        onSelect={(template: ReportTemplate) => {
                            capture('template_selected', {
                                template_source:
                                    template.source === 'laudopsi' ? 'system' : 'custom',
                            });
                            void applyTemplate(template, patientData);
                        }}
                    />
                ),
                size: 'xl',
            });
        } else {
            const dismissed = window.localStorage.getItem('template_upsell_dismissed');
            if (!dismissed || dismissed !== 'true') {
                openModal({
                    title: 'Dica',
                    content: <TemplateUpsellModal onClose={closeModal} />,
                });
            }
        }
    };

    const openPendingTemplatePrompt = () => {
        if (!pendingTemplatePrompt) return;

        setPendingTemplatePrompt(null);
        openTemplatePrompt(pendingTemplatePrompt.templates, pendingTemplatePrompt.patientData);
    };

    const applyTemplate = async (template: ReportTemplate, patientData: Patient | undefined) => {
        if (template.source === 'laudopsi' && platformTemplateAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(platformTemplateAccess.decision));
            closeModal();
            return;
        }

        let templateToApply = template;
        try {
            if (template.source === 'laudopsi') {
                templateToApply = await reportsApi.getTemplate(template.id);
            }
        } catch (error) {
            console.error('Failed to load full template', error);
            toast.error('Erro ao carregar template');
            closeModal();
            return;
        }

        setValues(
            buildReportDataFromTemplate(templateToApply, {
                patient: patientData,
                cover: autoAppliedCoverRef.current ?? undefined,
            }),
        );
        closeModal();
        toast.success('Modelo aplicado com sucesso!');
    };

    return {
        isLoading,
        hasPendingTemplatePrompt: Boolean(pendingTemplatePrompt),
        openPendingTemplatePrompt,
    };
};
